import type {
  PortalMetric,
  PortalReportLabels,
  PortalSeriesPoint,
} from '../../types/portalReport.js';
import { connectableSegments, isSpendWithoutLeads } from './series.js';

// CLIENT PORTAL round 2, item 6 — shaping the report's series for the SHARED
// MultiTrendChart. PURE; no I/O, no recharts.
//
// ⚠️ AN ADAPTER, NOT A THIRD CHART. Both staff dashboards already plot several
// metrics on one canvas through MultiTrendChart; this hands it the portal's
// series in the shape it consumes, exactly as the Meta and Google adapters do.
// That is what keeps the honesty rules where they already live — the chart never
// divides anything and never substitutes a zero.

/**
 * ⚠️⚠️ THE MAGNITUDE PROBLEM, AND WHAT WE DO ABOUT IT.
 *
 * The three metrics live on wildly different scales: a CPL around $10, leads
 * around 50, spend around $1,500. Plotted on one axis, CPL and leads collapse
 * onto the floor and the chart says nothing about them.
 *
 * The split is by MAGNITUDE CLASS, not by metric identity:
 *   · SPEND is the big one and takes the LEFT axis.
 *   · LEADS and CPL are the small ones and share the RIGHT axis.
 *
 * ⚠️ Leads and CPL share an axis because they are usually within an order of
 * magnitude of each other, while spend is one to two orders above both. Giving
 * each metric its own axis is not an option — recharts supports two, and three
 * stacked scales on one canvas is unreadable in a different way.
 *
 * ⚠️⚠️ AND THE HONEST LIMIT, WHICH THE UI STATES RATHER THAN HIDES: with all
 * three selected, leads and CPL still share one axis, so if they happen to be
 * far apart (50 leads at a $400 CPL) one of them is still squashed. Two axes
 * cannot separate three scales. `multiTrendLegibilityWarning` returns the
 * sentence the chart shows when that is actually the case — measured from the
 * data, not assumed — so the reader knows to drop a series rather than
 * mis-reading a flat line as a flat metric. The brief asked us to say so rather
 * than ship it unreadable; this is saying so.
 */
export function axisFor(metric: PortalMetric): 'left' | 'right' {
  return metric === 'spend' ? 'left' : 'right';
}

/** The ratio past which two series on one axis stop being readable together. */
export const LEGIBILITY_RATIO = 25;

function maxOf(series: readonly PortalSeriesPoint[], metric: PortalMetric): number {
  let max = 0;
  for (const p of series) {
    const v = metric === 'leads' ? p.leads : metric === 'spend' ? p.spend : p.costPerLead;
    if (v != null && v > max) max = v;
  }
  return max;
}

/**
 * The warning to show, or null when the chart is fine as drawn.
 *
 * ⚠️ MEASURED FROM THE ACTUAL DATA. A blanket "three metrics may be hard to
 * read" note would cry wolf on the common case where leads and CPL sit happily
 * together; this fires only when the two right-axis series really are far enough
 * apart that one is flattened.
 */
export function multiTrendLegibilityWarning(
  series: readonly PortalSeriesPoint[],
  metrics: readonly PortalMetric[],
  labels: PortalReportLabels,
): string | null {
  const right = metrics.filter((m) => axisFor(m) === 'right');
  if (right.length < 2) return null;
  const values = right.map((m) => ({ metric: m, max: maxOf(series, m) }));
  const biggest = values.reduce((a, b) => (b.max > a.max ? b : a));
  const smallest = values.reduce((a, b) => (b.max < a.max ? b : a));
  if (smallest.max <= 0) return null;
  if (biggest.max / smallest.max < LEGIBILITY_RATIO) return null;
  const nameOf = (m: PortalMetric): string =>
    m === 'leads' ? labels.leads : m === 'spend' ? labels.spend : labels.costPerLead;
  return `${nameOf(smallest.metric)} is much smaller than ${nameOf(
    biggest.metric,
  )}, so it looks flat on a shared scale. Untick one to read it properly.`;
}

export interface PortalTrendSeriesInput {
  metric: PortalMetric;
  label: string;
  type: 'line' | 'bar';
  /** Per-bucket formatted figure — the AUTHORITATIVE string for the tooltip. */
  formatted: (point: PortalSeriesPoint) => string;
}

export interface BuiltTrendSeries {
  key: string;
  label: string;
  type: 'line' | 'bar';
  axis: 'left' | 'right';
  continuationOf?: string;
  points: Array<{ value: number | null; formatted: string }>;
}

function valueOf(point: PortalSeriesPoint, metric: PortalMetric): number | null {
  if (metric === 'leads') return point.leads;
  if (metric === 'spend') return point.spend;
  return point.costPerLead;
}

/**
 * Build the chart series for one metric, split into connectable segments.
 *
 * ⚠️ SEGMENTS CARRY THE ROUND-2 CPL RULE INTO THE MULTI-METRIC CHART. A line
 * must break at an unmeasured bucket (we know nothing) but join across a
 * measured day that spent and returned no leads (we know exactly what happened).
 * One `connectNulls` flag is all-or-nothing, so a segment per connectable run is
 * how both hold — the same device the single-metric chart uses.
 *
 * ⚠️ A BAR series is NOT segmented: bars are discrete, so there is no line to
 * break and no join to make. Segmenting them would produce several identical bar
 * series and several legend entries for one metric.
 */
export function buildTrendSeries(
  series: readonly PortalSeriesPoint[],
  input: PortalTrendSeriesInput,
): BuiltTrendSeries[] {
  const axis = axisFor(input.metric);
  const points = series.map((p) => ({
    value: valueOf(p, input.metric),
    formatted: input.formatted(p),
  }));

  if (input.type === 'bar') {
    return [{ key: input.metric, label: input.label, type: 'bar', axis, points }];
  }

  const segments = connectableSegments(series);
  if (segments.length === 0) {
    // Nothing measured at all — one empty series keeps the legend honest about
    // what was asked for rather than silently dropping the metric.
    return [
      {
        key: input.metric,
        label: input.label,
        type: 'line',
        axis,
        points: points.map((p) => ({ ...p, value: null })),
      },
    ];
  }

  return segments.map((segment, i) => ({
    key: i === 0 ? input.metric : `${input.metric}__${i}`,
    label: input.label,
    type: 'line' as const,
    axis,
    ...(i === 0 ? {} : { continuationOf: input.metric }),
    points: points.map((p, idx) =>
      idx >= segment.from && idx <= segment.to ? p : { value: null, formatted: p.formatted },
    ),
  }));
}

/** Buckets that spent money and returned nothing — marked, never plotted as 0. */
export function spendWithoutLeadsBuckets(series: readonly PortalSeriesPoint[]): string[] {
  return series.filter(isSpendWithoutLeads).map((p) => p.bucket);
}
